/**
 * 掲載中のメニュー — 紐付いたブランドの「いま kalori に公開されている」商品（読み取り）。
 *
 * 栄養は項目ごとに出どころ（公式サイト / AI 推定 / 御社入力）を出す。⚠️ 商品単位の 1 ラベルにしない —
 * 「公式」の商品でも PFC が推定のことが普通にある（`src/domain/catalog-view.ts` の実測）。
 * ⚠️ 一括ダウンロードは付けない（まとめて抜ける口にしない・同ファイルのヘッダ）。
 */
import { useEffect, useState, type FormEvent } from "react";
import { api, type CatalogItem, type LinkedChain } from "../api";
import { Link, navigate } from "../router";
import { REVIEW_STATE_LABEL, fmtNumber, fmtYen, sourceLabel } from "../format";
import { SourceBadge } from "./parts";

const PAGE = 50;

export function Catalog({ query }: { query: URLSearchParams }) {
  const shop = query.get("shop") ?? "";
  const q = query.get("q") ?? "";
  const attention = query.get("attention") === "1";
  const [chains, setChains] = useState<LinkedChain[]>([]);
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<{ text: string; unlinked: boolean } | null>(null);
  const [draft, setDraft] = useState(q);
  const [loadingMore, setLoadingMore] = useState(false);
  const [attentionMeta, setAttentionMeta] = useState<{ total: number; truncated: boolean } | null>(null);

  useEffect(() => {
    void api.chains().then((r) => r.ok && setChains(r.data));
  }, []);

  useEffect(() => {
    setDraft(q);
    setItems(null);
    setError(null);
    let alive = true;
    void api.catalog({ shopId: shop || undefined, q: q || undefined, attention, limit: PAGE }).then((r) => {
      if (!alive) return;
      if (r.ok) {
        setItems(r.data.items);
        setHasMore(r.data.hasMore);
        setAttentionMeta(r.data.attention);
      } else {
        setError({ text: r.error, unlinked: r.code === "merchant_unlinked" });
      }
    });
    return () => {
      alive = false;
    };
  }, [shop, q, attention]);

  const more = async () => {
    if (!items) return;
    setLoadingMore(true);
    const r = await api.catalog({ shopId: shop || undefined, q: q || undefined, attention, limit: PAGE, offset: items.length });
    setLoadingMore(false);
    if (r.ok) {
      setItems([...items, ...r.data.items]);
      setHasMore(r.data.hasMore);
    }
  };

  const go = (next: { shop?: string; q?: string; attention?: boolean }) => {
    const p = new URLSearchParams();
    const s = next.shop ?? shop;
    const qq = next.q ?? q;
    if (s) p.set("shop", s);
    if (qq) p.set("q", qq);
    if (next.attention ?? attention) p.set("attention", "1");
    navigate(`/catalog${p.size ? `?${p}` : ""}`);
  };
  const search = (e: FormEvent) => {
    e.preventDefault();
    go({ q: draft.trim() });
  };

  const chainName = (id: string) => chains.find((c) => c.shopId === id)?.name ?? id;

  return (
    <section className="stack-lg">
      <div>
        <h1>掲載中のメニュー</h1>
        <p className="muted">kalori のアプリとサイトに、いま公開されている御社のメニューです。直したい品を選んで公式値を入力できます。</p>
      </div>

      <div className="toolbar">
        {chains.length > 1 && (
          <select value={shop} onChange={(e) => go({ shop: e.target.value })} aria-label="ブランド">
            <option value="">すべてのブランド</option>
            {chains.map((c) => (
              <option key={c.shopId} value={c.shopId}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <form onSubmit={search} className="search">
          <input type="search" placeholder="メニュー名で探す" value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="メニュー名で探す" />
          <button className="btn">検索</button>
        </form>
        <label className="toggle">
          <input type="checkbox" checked={attention} onChange={(e) => go({ attention: e.target.checked })} />
          確認をおすすめの品だけ
        </label>
      </div>

      {attention && attentionMeta && (
        <p className="notice">
          エネルギーと、たんぱく質・脂質・炭水化物から計算した値（4×P + 9×F + 4×C）が 30% 以上ずれている品を、ずれの大きい順に並べています（
          {attentionMeta.total.toLocaleString("ja-JP")} 品）。どれかの値が誤っている可能性が高いので、公式値の入力をおすすめします。
          {attentionMeta.truncated && " メニューが多いため、一部だけを調べています。ブランドや検索語で絞ると全体を調べられます。"}
        </p>
      )}

      {error &&
        (error.unlinked ? (
          <p className="notice">
            まだブランドが紐付いていません。<Link href="/">ホーム</Link>から紐付けるか、運営へご連絡ください。
          </p>
        ) : (
          <p className="error">{error.text}</p>
        ))}
      {!error && items === null && <p className="muted">読み込んでいます…</p>}
      {items && items.length === 0 && (
        <p className="muted">
          {attention
            ? "値どうしが噛み合っていない品は見つかりませんでした。"
            : q
              ? `「${q}」に当てはまるメニューはありません。`
              : "公開中のメニューはありません。"}
        </p>
      )}

      {items && items.length > 0 && (
        <div className="card flush">
          <table className="table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th className="num">エネルギー</th>
                <th className="num hide-sm">たんぱく質</th>
                <th className="num hide-sm">脂質</th>
                <th className="num hide-sm">炭水化物</th>
                <th className="num" title="利用者が「このお店に公式値を希望する」を押した件数">公式値の希望</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.productId} className="clickable" onClick={() => navigate(`/catalog/${it.productId}`)}>
                  <td>
                    <Link href={`/catalog/${it.productId}`} className="row-title">
                      {it.name}
                    </Link>
                    <div className="muted small">
                      {chains.length > 1 && `${chainName(it.shopId)} ・ `}
                      {fmtYen(it.price)}
                    </div>
                  </td>
                  <NutrientCell value={it.nutrition.cal} unit="kcal" source={it.provenance.cal} />
                  <NutrientCell value={it.nutrition.protein} unit="g" source={it.provenance.protein} hideSm />
                  <NutrientCell value={it.nutrition.fat} unit="g" source={it.provenance.fat} hideSm />
                  <NutrientCell value={it.nutrition.carbs} unit="g" source={it.provenance.carbs} hideSm />
                  <td className="num">
                    {it.officialRequests ? `${it.officialRequests.toLocaleString("ja-JP")} 件` : <span className="muted small">—</span>}
                  </td>
                  <td>
                    {it.entry ? (
                      <span className="chip">{entryLabel(it.entry)}</span>
                    ) : it.attention ? (
                      <span className="chip state-attention" title="エネルギーと PFC が噛み合っていません">
                        確認をおすすめ
                      </span>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <button className="btn" disabled={loadingMore} onClick={() => void more()}>
          {loadingMore ? "読み込んでいます…" : "さらに表示"}
        </button>
      )}
      <p className="muted small">
        出どころの見方: <SourceBadge source="official" /> 御社サイトの掲載値を kalori が読み取ったもの ／ <SourceBadge source="estimated" /> 写真・原材料から
        kalori が推定したもの ／ <SourceBadge source="operator" /> 御社が入力し、承認済みのもの
      </p>
    </section>
  );
}

function entryLabel(entry: NonNullable<CatalogItem["entry"]>): string {
  if (entry.status === "draft") return entry.submittedAt ? REVIEW_STATE_LABEL.pending_review : REVIEW_STATE_LABEL.editing;
  return REVIEW_STATE_LABEL[entry.status as keyof typeof REVIEW_STATE_LABEL] ?? entry.status;
}

function NutrientCell({ value, unit, source, hideSm }: { value: number | null; unit: string; source: string | null; hideSm?: boolean }) {
  const label = sourceLabel(source);
  return (
    <td className={hideSm ? "num hide-sm" : "num"}>
      <span className={`value tone-${label.tone}`} title={label.text}>
        {fmtNumber(value, unit)}
      </span>
    </td>
  );
}
